// LayoutEditorLayoutContainer.jsx

import React from 'react';

// CSS
import './PageEditorLayoutContainer.css';

// Components
import LayoutEditorInsertSequence from '../insertsequence/LayoutEditorInsertSequence';

/**
 * LayoutEditorLayoutContainer
 *
 * Renders the draggable row or stack shell around a layout container and its child insert sequence.
 *
 * @param {object} containerNode - Layout container node being rendered.
 * @param {array} containerChildren - Ordered child node keys for the container.
 * @param {string} childOrientation - Orientation used for child insert and drop behavior.
 * @param {boolean} isDragging - Whether this container is currently being dragged.
 * @param {boolean} isDropActive - Whether this container is the active drop target.
 * @param {boolean} suspendDrag - Whether drag should be disabled for inline content editing.
 * @param {function|null} getInsertButtonProps - Returns extra props for a given insert button index.
 * @param {function} onBlurCapture - Blur handler for clearing toolbar focus.
 * @param {function} onContainerClick - Click handler for opening the toolbar.
 * @param {function} onDragEnd - Drag end handler.
 * @param {function} onDragOver - Drag over handler.
 * @param {function} onDragStart - Drag start handler.
 * @param {function} onDrop - Drop handler.
 * @param {function} onInsertAt - Insert handler for child positions.
 * @param {function} renderChild - Child render callback.
 *
 * @returns {ReactNode} The rendered layout container.
 */
const LayoutEditorLayoutContainer = ({
	containerNode,
	containerChildren = [],
	childOrientation = 'vertical',
	isDragging = false,
	isDropActive = false,
	suspendDrag = false,
	getInsertButtonProps = null,
	onBlurCapture,
	onContainerClick,
	onDragEnd,
	onDragOver,
	onDragStart,
	onDrop,
	onInsertAt,
	renderChild,
}) => {
	const isRow = containerNode?.kind === 'row';
	const rootClassName = 'layout-editor-layout-container-root';
	const childrenClassName = [
		'layout-editor-layout-container-children',
		isRow ? 'layout-editor-layout-container-children-row' : 'layout-editor-layout-container-children-stack',
	].filter(Boolean).join(' ');
	const cellClassName = [
		'layout-editor-layout-container-cell',
		isRow ? 'layout-editor-layout-container-cell-row' : 'layout-editor-layout-container-cell-stack',
	].filter(Boolean).join(' ');

	const isDragEnabled = !suspendDrag;
	// ----- RENDER ----- //

	return (
		<div
			className={[
				rootClassName,
				suspendDrag ? 'layout-editor-layout-container-root-suspend-drag' : '',
			].filter(Boolean).join(' ')}
			style={{
				cursor: suspendDrag ? 'default' : (isDragging ? 'grabbing' : 'grab'),
				opacity: isDragging ? 0.45 : 1,
			}}
			draggable={isDragEnabled}
			onBlurCapture={onBlurCapture}
			onClick={onContainerClick}
			onDragEnd={onDragEnd}
			onDragOver={onDragOver}
			onDragStart={onDragStart}
			onDrop={onDrop}
		>
			<div className={childrenClassName}>
				<LayoutEditorInsertSequence
					childKeys={containerChildren}
					getInsertButtonProps={getInsertButtonProps}
					keyPrefix={containerNode?.key}
					orientation={childOrientation}
					renderChild={(childKey, childIndex) => {
						if (typeof renderChild !== 'function') return null;

						return (
							<div className={cellClassName} key={childKey}>
								{renderChild(childKey, childIndex)}
							</div>
						);
					}}
					onInsertAt={onInsertAt}
				/>
			</div>
		</div>
	);
};

// Dev Tools
LayoutEditorLayoutContainer.displayName = 'LayoutEditorLayoutContainer';

export default LayoutEditorLayoutContainer;
